import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AgGridModule, ICellRendererAngularComp } from 'ag-grid-angular';
import { ClientSideRowModelModule, ColumnAutoSizeModule, CustomFilterModule, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, Module, PaginationModule, RowSelectionModule, RowSelectionOptions, TextFilterModule, ValidationModule } from 'ag-grid-community';
import { OrganizationService } from '../../services/organization.service';
import { IntentService } from '../../services/intent.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-organization-intent',
  imports: [FormsModule, CommonModule, AgGridModule],
  templateUrl: './organization-intent.component.html',
  styleUrl: './organization-intent.component.scss'
})
export class OrganizationIntentComponent implements OnInit {
  @ViewChild('toggleConfirmModal') toggleModal!: any;
  @ViewChild('bulkToggleModal') bulkToggleModal!: any;


  modules = [ClientSideRowModelModule, ValidationModule, CustomFilterModule, TextFilterModule, ColumnAutoSizeModule, RowSelectionModule, PaginationModule];
  gridApi: GridApi | undefined;
  gridOptions: GridOptions | undefined;
  rowSelection: any = { mode: 'multiRow' };


  isLoadingAction = false;

  organizations: any[] = [];
  intents: any[] = [];
  filteredIntents: any[] = [];
  organizationIntents: any[] = [];
  groups: any[] = [];
  selectedOrganizationId: any;
  selectedGroup: string | null = null;
  searchQuery: string = '';
  isLoadingIntents: boolean = false;
  selectedIntent: any = null;

  // Bulk selection properties
  bulkToggleAction: 'include' | 'exclude' = 'include';

  constructor(private organizationService: OrganizationService,
    private intentService: IntentService, private modalService: NgbModal) {
    this.gridOptions = this.initializeGridOptions();
  }

  ngOnInit(): void {
    this.loadOrganizations();
    this.loadGroups();
  }


  loadOrganizations() {
    this.isLoadingAction = true;
    this.organizationService.getAll().subscribe({
      next: (data: any[]) => this.organizations = data,
      complete: () => this.isLoadingAction = false,
      error: (error: any) => {
        console.error('Error fetching organizations:', error);
        this.isLoadingAction = false;
      }
    });
  }

  loadGroups() {
    this.isLoadingAction = true;
    this.intentService.getIntentGroups().subscribe({
      next: (groups) => this.groups = groups,
      complete: () => this.isLoadingAction = false,
      error: (error) => {
        console.error('Error loading groups:', error)
        this.isLoadingAction = false;
      }
    });
  }

  loadIntents() {
    if (!this.selectedOrganizationId) {
      this.intents = [];
      this.filteredIntents = [];
      this.organizationIntents = [];
      this.gridApi?.setGridOption('rowData', []);
      this.gridApi?.deselectAll();
      return;
    }

    console.log('Loading intents for organization:', this.selectedOrganizationId);
    this.isLoadingIntents = true;
    this.intentService.getAll().subscribe({
      next: (data: any) => {
        this.intents = data.map((intent: any) => {
          return {
            ...intent,
            isIncluded: this.computeIsIncluded(intent.id),
          }
        });

        this.loadOrganizationIntents();
      },
      error: (error: any) => {
        console.error('Error fetching intents:', error);
        this.isLoadingIntents = false;
      }
    });
  }

  loadOrganizationIntents() {
    this.organizationService.getOrganizationIntents(this.selectedOrganizationId).subscribe({
      next: (data: any) => {

        console.log('Organization Intents:', data);
        this.organizationIntents = data;
        this.updateIntentInclusion();
        this.filterIntents();
      },
      error: (error: any) => {
        console.error('Error fetching organization intents:', error);
      },
      complete: () => {
        this.isLoadingIntents = false;
      }
    });
  }

  computeIsIncluded(intentId: any) {
    const orgIntent = this.organizationIntents.find(oi => oi.intentId === intentId);
    if (orgIntent) {
      return orgIntent.isIncluded;
    }
    const intent = this.intents.find(i => i.id === intentId) || this.filteredIntents.find(i => i.id === intentId);
    return intent?.groupName !== 'Custom';
  }

  updateIntentInclusion() {
    this.intents = this.intents.map(intent => ({
      ...intent,
      isIncluded: this.computeIsIncluded(intent.id)
    }));
    this.filterIntents();
  }

  filterIntents() {
    let filtered = [...this.intents]

    if (this.selectedGroup) {
      filtered = filtered.filter((intent) => intent.groupName === this.selectedGroup);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter((intent) => {
        return Object.values(intent).some(value => value?.toString().toLowerCase().includes(query));
      });
    }

    this.filteredIntents = filtered;
    this.gridApi?.setGridOption('rowData', this.filteredIntents);
    this.gridApi?.deselectAll(); // Clear selection on filter change
  }

  selectGroup(group: string | null): void {
    this.selectedGroup = group;
    this.filterIntents();
  }

  clearSearch() {
    this.searchQuery = '';
    this.filterIntents();
  }


  //======== GRID
  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  initializeGridOptions(): GridOptions {
    return {
      columnDefs: [              
        { field: 'description', sortable: true, filter: true, resizable: true, flex: 1 },
        { field: 'groupName', headerName: 'Group', sortable: true, filter: true, resizable: true, width: 150 },
        {
          field: 'status',
          headerName: 'Status',
          sortable: true,
          filter: true,
          resizable: true,
          width: 120,
          valueGetter: (params) => params.data.isIncluded ? 'Included' : 'Excluded'
        },
        {
          field: 'id',
          headerName: 'Actions',
          headerClass: 'header-center',
          cellRenderer: IntentToggleCellRenderer,
          cellRendererParams: {
            context: {
              onToggleAction: this.openToggleModal.bind(this)
            }
          },
          resizable: true,
          width: 110,
          pinned: 'right'
        }
      ],
      onColumnVisible: () => {
        this.gridApi?.autoSizeAllColumns();
      }
    };
  }

  onSelectionChanged() {
    this.gridApi?.refreshHeader(); // Ensure header reflects selection state
  }
  //========



  openToggleModal(item: any): void {
    this.selectedIntent = item;
    this.modalService.open(this.toggleModal, { centered: true });
  }

  confirmToggleIntent(modal: any) {
    if (!this.selectedIntent || !this.selectedOrganizationId) return;

    this.organizationService.toggleOrganizationIntent(
      this.selectedOrganizationId,
      this.selectedIntent.id,
      !this.selectedIntent.isIncluded
    ).subscribe({
      next: () => this.loadOrganizationIntents(),
      error: (error) => console.error('Error toggling intent:', error),
      complete: () => {
        this.selectedIntent = null;
        modal.close();
      }
    });
  }

  // Bulk Selection Methods
  hasSelectedIntents(): boolean {
    return (this.gridApi?.getSelectedRows().length || 0) > 0;
  }

  getSelectedIntentsCount(): number {
    return this.gridApi?.getSelectedRows().length || 0;
  }

  openBulkToggleModal(content: any): void {
    this.bulkToggleAction = 'include'; // Default action
    this.modalService.open(content, { centered: true });
  }

  confirmBulkToggle(modal: any): void {
    if (!this.selectedOrganizationId) return;

    const selectedIntents = this.gridApi?.getSelectedRows() || [];
    const intentIds = selectedIntents.map(intent => intent.id);
    const isIncluded = this.bulkToggleAction === 'include';

    if (intentIds.length === 0) {
      alert('No intents selected.');
      return;
    }

    this.isLoadingAction = true;
    this.organizationService.bulkToggleOrganizationIntents(
      this.selectedOrganizationId,
      intentIds,
      isIncluded
    ).subscribe({
      next: () => {
        this.loadOrganizationIntents();
        modal.close();
      },
      error: (error) => {
        console.error('Error toggling intents:', error);
        alert('An error occurred while toggling the selected intents.');
        this.isLoadingAction = false;
      },
      complete: () => {
        this.isLoadingAction = false;
        this.gridApi?.deselectAll(); // Clear selection after action
      }
    });
  }

}




@Component({
  standalone: true,
  template: `
    <button class="btn btn-sm btn-primary" (click)="onToggle()">
      {{ params.data.isIncluded ? 'Exclude' : 'Include' }} 
    </button>
  `
})
export class IntentToggleCellRenderer implements ICellRendererAngularComp {
  params!: ICellRendererParams;

  agInit(params: ICellRendererParams): void {
    this.params = params;
  }

  onToggle() {
    this.params.context.onToggleAction(this.params.data);
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    return true;
  }
}